const API_BASE = '/api';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: options?.body != null ? { 'Content-Type': 'application/json' } : {},
    ...options,
  });

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Server error (${res.status}). Please try again later.`);
  }

  if (!res.ok) {
    // Safe: error responses are JSON objects with an optional string `error` field.
    const errorMessage = (data as { error?: string })?.error;
    throw new Error(errorMessage || 'Something went wrong');
  }

  return data as T; // Safe: callers specify the expected shape via the generic type parameter.
}

export function createCheckout(email: string) {
  return request<{ url: string }>('/checkout', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function redeemCode(email: string, code: string) {
  return request<{ success: boolean; message: string }>('/redeem', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
}

export function subscribeNewsletter(email: string) {
  return request<{ success: boolean; message: string }>('/newsletter', {
    method: 'POST',
    body: JSON.stringify({ email, source: 'landing_page' }),
  });
}

// --- Admin API ---

export function adminLogin(email: string, password: string) {
  return request<{ success: boolean; email: string }>('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function adminLogout() {
  return request<{ success: boolean }>('/admin/logout', { method: 'POST' });
}

export function adminGetMe() {
  return request<{ email: string }>('/admin/me');
}

export interface AdminStats {
  totalPurchases: number;
  totalRevenue: number;
  totalSubscribers: number;
  totalCodes: number;
  usedCodes: number;
  availableCodes: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface PurchaseRow {
  id: string;
  email: string | null;
  amountCents: number;
  currency: string;
  status: string;
  downloadCount: number;
  maxDownloads: number;
  squarePaymentId: string;
  createdAt: string;
}

export interface SubscriberRow {
  id: string;
  email: string;
  status: string;
  source: string;
  subscribedAt: string;
}

export interface CodeRow {
  id: string;
  code: string;
  assignedEmail: string | null;
  redeemedAt: string | null;
  downloadCount: number;
  createdAt: string;
}

export function adminGetStats() {
  return request<AdminStats>('/admin/stats');
}

export function adminGetPurchases(page = 1) {
  return request<PaginatedResponse<PurchaseRow>>(`/admin/purchases?page=${page}`);
}

export function adminGetSubscribers(page = 1) {
  return request<PaginatedResponse<SubscriberRow>>(`/admin/subscribers?page=${page}`);
}

export function adminGetCodes(page = 1) {
  return request<PaginatedResponse<CodeRow>>(`/admin/codes?page=${page}`);
}
